"use strict";

/* ABMIUM homepage runtime: only behaviour used by the homepage shell.
   Product/collection/quick-order controllers remain in scripts.js on their own routes. */

const isTouchDevice = () => "ontouchstart" in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0;

function debounce(func, wait, immediate) {
  let timeout;
  return function executedFunction() {
    const context = this;
    const args = arguments;
    const later = function () {
      timeout = null;
      if (!immediate) func.apply(context, args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func.apply(context, args);
  };
}

function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: `application/${type}` },
  };
}

function getUniqueSectionNames(sectionsToRender) {
  return [...new Set(sectionsToRender.map((section) => section.section).filter(Boolean))];
}

function isSectionRenderTargetPresent(target) {
  const element = document.getElementById(target.id);
  if (!element) return false;
  if (!target.selector) return true;
  return element.matches(target.selector) || !!element.querySelector(target.selector);
}

function getFocusableElements(container) {
  return Array.from(container.querySelectorAll("summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"));
}

const trapFocusHandlers = {};
function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);
  if (elementToFocus) elementToFocus.focus();
}

function trapFocus(container, elementToFocus = container) {
  const elements = getFocusableElements(container);
  const first = elements[0];
  const last = elements[elements.length - 1];
  removeTrapFocus();
  if (!first || !last) return;
  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;
    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };
  trapFocusHandlers.focusout = () => document.removeEventListener("keydown", trapFocusHandlers.keydown);
  trapFocusHandlers.keydown = (event) => {
    if (event.code.toUpperCase() !== "TAB") return;
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };
  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);
  elementToFocus?.focus?.();
}

function pauseAllMedia() {
  document.querySelectorAll(".js-youtube").forEach((video) => video.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', "*"));
  document.querySelectorAll(".js-vimeo").forEach((video) => video.contentWindow?.postMessage('{"method":"pause"}', "*"));
  document.querySelectorAll("video").forEach((video) => video.pause());
}
window.pauseAllMedia = pauseAllMedia;

/* Touch navigation */
if (isTouchDevice()) {
  const subMenuNavs = document.querySelectorAll(".has-sub-menu > a");
  subMenuNavs.forEach((mainMenuNav) => {
    mainMenuNav.addEventListener("click", (event) => {
      subMenuNavs.forEach((item) => {
        if (item !== mainMenuNav) item.classList.remove("is-dropdown-open");
      });
      if (mainMenuNav.classList.contains("is-dropdown-open")) return;
      event.preventDefault();
      mainMenuNav.classList.add("is-dropdown-open");
    });
  });
}

/* Mobile menu */
const mobileMenuWrap = document.querySelector(".head-slot-nav-mobile-link-wrapper");
const mobileNavWrap = document.querySelector(".navigation-mobile-wrap");
const mobileMenuToggle = document.querySelector(".head-slot-nav-mobile-link");
const mobileMenu = document.querySelector(".mobile-menu");
const expandOnButton = mobileMenu?.dataset.buttonExpand != null;

function setMobileMenuMaxheight() {
  if (!mobileMenu) return;
  const header = document.querySelector(".header, #header");
  const bottom = header?.getBoundingClientRect().bottom || 0;
  mobileMenu.style.maxHeight = `${Math.max(240, window.innerHeight - bottom + 70)}px`;
}
function handleMobileMenuOpen() {
  if (!mobileMenu || !mobileMenuToggle) return;
  document.querySelector("sticky-header")?.reveal?.();
  document.body.classList.add("mobile-menu-open");
  mobileMenuToggle.ariaExpanded = "true";
  mobileMenu.ariaHidden = "false";
  if (mobileMenuWrap && mobileNavWrap) trapFocus(mobileMenuWrap, mobileNavWrap);
  setMobileMenuMaxheight();
  document.dispatchEvent(new CustomEvent("mobile-menu:open"));
}
function handleMobileMenuDismiss() {
  if (!mobileMenu || !mobileMenuToggle) return;
  document.body.classList.remove("mobile-menu-open");
  mobileMenuToggle.ariaExpanded = "false";
  mobileMenu.ariaHidden = "true";
  removeTrapFocus(mobileMenuToggle);
  mobileMenu.style.maxHeight = "";
  document.dispatchEvent(new CustomEvent("mobile-menu:close"));
}

document.body.addEventListener("click", (event) => {
  const target = event.target;
  const mobileNav = target.closest?.(".navigation-mobile-item");
  const mobileButton = target.closest?.(".head-slot-nav-mobile-link");
  const anchor = target.closest?.('a[href^="#"]');

  if (mobileNav && mobileNav.querySelector("ul")) {
    if (expandOnButton) {
      if (target.closest?.(".navigation-mobile-item-link-expand")) {
        event.preventDefault();
        mobileNav.classList.toggle("menu-item-expanded");
      }
    } else if (target.closest?.(".navigation-mobile-item-link")) {
      event.preventDefault();
      mobileNav.classList.toggle("menu-item-expanded");
    }
    const expanded = mobileNav.classList.contains("menu-item-expanded");
    const button = mobileNav.querySelector(".navigation-mobile-item-link-expand");
    if (button) button.setAttribute("aria-expanded", expanded ? "true" : "false");
  }

  if (mobileButton) {
    event.preventDefault();
    document.body.classList.contains("mobile-menu-open") ? handleMobileMenuDismiss() : handleMobileMenuOpen();
    return;
  }
  if (anchor && document.body.classList.contains("mobile-menu-open")) handleMobileMenuDismiss();
  if (document.body.classList.contains("mobile-menu-open") && !target.closest?.(".mobile-menu") && !mobileButton) handleMobileMenuDismiss();
});

/* Deferred video */
if (!customElements.get("deferred-media")) {
  customElements.define("deferred-media", class DeferredMedia extends HTMLElement {
    constructor() {
      super();
      this.querySelector('[id^="Deferred-Poster-"]')?.addEventListener("click", () => this.loadContent());
    }
    loadContent(focus = true) {
      window.pauseAllMedia();
      if (this.hasAttribute("loaded")) return;
      const template = this.querySelector("template");
      if (!template?.content?.firstElementChild) return;
      const content = document.createElement("div");
      content.appendChild(template.content.firstElementChild.cloneNode(true));
      this.setAttribute("loaded", true);
      const media = content.querySelector("video, model-viewer, iframe");
      if (!media) return;
      const deferredElement = this.appendChild(media);
      if (focus) deferredElement.focus?.();
    }
  });
}

/* Drawer controls */
if (!customElements.get("drawer-modal")) {
  customElements.define("drawer-modal", class DrawerModal extends HTMLElement {
    constructor() {
      super();
      this.bodyClass = this.getAttribute("body-class");
      this.isOpen = false;
      this.eventPrefix = this.dataset.eventPrefix;
      this.addEventListener("keyup", (event) => {
        if (event.code.toUpperCase() === "ESCAPE" && this.isOpen) this.close();
      });
    }
    open(opener) {
      this.isOpen = true;
      this.openedBy = opener;
      if (this.bodyClass) document.body.classList.add(this.bodyClass);
      document.body.classList.add("drawer-open");
      if (this.eventPrefix) document.dispatchEvent(new CustomEvent(`${this.eventPrefix}:open`));
    }
    close() {
      this.isOpen = false;
      if (this.bodyClass) document.body.classList.remove(this.bodyClass);
      document.body.classList.remove("drawer-open");
      if (this.openedBy) removeTrapFocus(this.openedBy);
      if (this.eventPrefix) document.dispatchEvent(new CustomEvent(`${this.eventPrefix}:close`));
    }
  });
}

if (!customElements.get("drawer-toggle")) {
  customElements.define("drawer-toggle", class DrawerToggle extends HTMLElement {
    constructor() {
      super();
      this.toggleElement = this.querySelector("a") || this.querySelector("button");
      this.preventOpen = this.getAttribute("prevent-open") === "true";
      if (!this.toggleElement || this.preventOpen) return;
      this.toggleElement.addEventListener("click", (event) => {
        event.preventDefault();
        this.handleToggle(this.toggleElement);
      });
    }
    handleToggle(opener) {
      const drawer = document.querySelector(`#${this.getAttribute("for")}`);
      if (!drawer) return;
      drawer.isOpen ? this.close() : this.open(opener);
    }
    open(opener) {
      const drawer = document.querySelector(`#${this.getAttribute("for")}`);
      if (!drawer) return;
      drawer.open(opener || this.toggleElement);
      trapFocus(drawer, drawer.querySelector(".drawer-header") || drawer);
    }
    close() {
      const drawer = document.querySelector(`#${this.getAttribute("for")}`);
      if (drawer?.isOpen) drawer.close();
    }
  });
}

/* Quantity controls used by the mini cart */
if (!customElements.get("quantity-input")) {
  customElements.define("quantity-input", class QuantityInput extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector("input");
      if (!this.input) return;
      this.changeEvent = new Event("change", { bubbles: true });
      this.querySelectorAll("button").forEach((button) => button.addEventListener("click", (event) => {
        event.preventDefault();
        const previousValue = this.input.value;
        event.currentTarget.name === "plus" ? this.input.stepUp() : this.input.stepDown();
        if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
      }));
    }
  });
}

if (!customElements.get("cart-remove-button")) {
  customElements.define("cart-remove-button", class CartRemoveButton extends HTMLElement {
    constructor() {
      super();
      this.addEventListener("click", (event) => {
        event.preventDefault();
        this.closest("mini-cart")?.updateQuantity?.(this.dataset.index, 0);
      });
    }
  });
}

if (!customElements.get("cart-consent-checkbox")) {
  customElements.define("cart-consent-checkbox", class CartConsentCheckbox extends HTMLElement {
    connectedCallback() {
      const key = "_CART_CONSENT_CHECKBOX_";
      const checkbox = this.querySelector('[type="checkbox"]');
      if (!checkbox) return;
      try { if (localStorage.getItem(key) === "checked") checkbox.checked = true; } catch (_) {}
      checkbox.addEventListener("change", () => {
        try { checkbox.checked ? localStorage.setItem(key, "checked") : localStorage.removeItem(key); } catch (_) {}
      });
    }
  });
}

/* Mini cart: server-rendered at load; refresh only when first opened or changed. */
if (!customElements.get("mini-cart")) {
  customElements.define("mini-cart", class MiniCart extends HTMLElement {
    constructor() {
      super();
      this.toggle = this.querySelector("drawer-toggle");
      this.drawer = this.querySelector("drawer-modal");
      this.eventContext = this.dataset.eventContext || "MiniCart";
      this.firstOpenRefreshStarted = false;
      this.debouncedOnChange = debounce((event) => {
        if (event.target.matches?.('[name="updates[]"]')) this.updateQuantity(event.target.dataset.index, event.target.value);
      }, 350);
      this.addEventListener("change", this.debouncedOnChange);
    }
    connectedCallback() {
      window.miniCart = this;
      document.addEventListener("open-mini-cart", (event) => this.open(event.detail?.opener || null));
      document.addEventListener("close-mini-cart", () => this.close());
      document.addEventListener("toggle-mini-cart", (event) => this.drawer?.isOpen ? this.close() : this.open(event.detail?.opener || null));
    }
    open(opener) {
      this.toggle?.open?.(opener);
      this.dispatchEvent(new CustomEvent("mini-cart:opened", { bubbles: true }));
      if (!this.firstOpenRefreshStarted && !window.Shopify?.designMode) {
        this.firstOpenRefreshStarted = true;
        this.updateSectionContents().catch(() => {});
      }
    }
    close() {
      this.toggle?.close?.();
      this.dispatchEvent(new CustomEvent("mini-cart:closed", { bubbles: true }));
    }
    getSectionsToRender() {
      return [
        { id: "header", section: document.getElementById("header")?.dataset.id, selector: ".head-slot-cart-link" },
        { id: "header-mini-cart-content", section: "header-mini-cart", selector: "#header-mini-cart-content" },
        { id: "header-mini-cart-footer", section: "header-mini-cart", selector: "#header-mini-cart-footer" },
        { id: "mobile-dock", section: document.getElementById("mobile-dock")?.dataset.id, selector: ".head-slot-cart-link" },
      ].filter(isSectionRenderTargetPresent);
    }
    getSectionInnerHTML(html, selector) {
      return new DOMParser().parseFromString(html, "text/html").querySelector(selector)?.innerHTML;
    }
    replaceSections(sections) {
      this.getSectionsToRender().forEach((section) => {
        const html = sections?.[section.section];
        if (!html) return;
        const replacement = this.getSectionInnerHTML(html, section.selector);
        if (replacement == null) return;
        const root = document.getElementById(section.id);
        if (!root) return;
        let containers = root.querySelectorAll(section.selector);
        if (root.matches?.(section.selector)) containers = [root];
        else if (!containers.length) containers = [root];
        containers.forEach((container) => { if (container) container.innerHTML = replacement; });
      });
    }
    updateSectionContents() {
      const names = getUniqueSectionNames(this.getSectionsToRender());
      if (!names.length) return Promise.resolve();
      return fetch(`${window.location.pathname}?sections=${names.join(",")}&v=${Date.now()}`)
        .then((response) => response.json())
        .then((response) => this.replaceSections(response));
    }
    updateQuantity(line, quantity) {
      this.enableLoading();
      const body = JSON.stringify({
        line,
        quantity,
        sections: getUniqueSectionNames(this.getSectionsToRender()),
        sections_url: window.location.pathname,
      });
      fetch(window.routes.cart_change_url, { ...fetchConfig(), body })
        .then((response) => response.json())
        .then((state) => {
          if (state.errors) throw new Error(state.errors);
          this.replaceSections(state.sections);
          const eventName = Number(quantity) === 0 ? "cart:item-remove" : "cart:item-quantity-update";
          document.dispatchEvent(new CustomEvent(eventName, { detail: { cart: state, context: this.eventContext } }));
          if (typeof publish === "function" && window.PUB_SUB_EVENTS?.cartUpdate) publish(window.PUB_SUB_EVENTS.cartUpdate, { source: "mini-cart", cartData: state });
        })
        .catch(() => {
          const error = document.getElementById("mini-cart-error");
          if (error) error.textContent = window.cartStrings?.error || "Unable to update the cart.";
        })
        .finally(() => this.disableLoading());
    }
    enableLoading() { this.querySelector(".mini-cart-wrap")?.classList.add("loading"); }
    disableLoading() { this.querySelector(".mini-cart-wrap")?.classList.remove("loading"); }
  });
}

document.body.addEventListener("click", (event) => {
  if (!document.body.classList.contains("drawer-open")) return;
  if (event.target.closest?.(".drawer") || event.target.closest?.("drawer-toggle")) return;
  document.querySelectorAll("drawer-modal").forEach((drawer) => { if (drawer.isOpen) drawer.close(); });
});
